// Firebase types - imported only for type annotations (stripped at compile time)
import type { FirebaseApp } from '@firebase/app';
import type { Messaging } from '@firebase/messaging';

// Firebase configuration - these will be set from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Cached instances - populated on first use via dynamic import
let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

// Lazily load and cache the Firebase app instance
async function getFirebaseApp(): Promise<FirebaseApp> {
  if (app) return app;
  const { initializeApp } = await import('@firebase/app');
  app = initializeApp(firebaseConfig);
  return app;
}

// Lazily load and cache the Firebase messaging instance
async function getMessagingInstance(): Promise<Messaging> {
  if (messaging) return messaging;
  const firebaseApp = await getFirebaseApp();
  const { getMessaging } = await import('@firebase/messaging');
  messaging = getMessaging(firebaseApp);
  return messaging;
}

// Initialize Firebase only if config is present
export const initializeFirebase = async (): Promise<boolean> => {
  if (app) return true;

  // Check if required config is present
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.log('Firebase not configured - push notifications disabled');
    return false;
  }

  try {
    await getFirebaseApp();

    // Check if messaging is supported (not in SSR, not in unsupported browsers)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      await getMessagingInstance();
      console.log('Firebase initialized successfully');
      return true;
    }
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    // intentional: infrastructure init failure, caller receives false and disables push notifications
  }

  return false;
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // Ensure Firebase is initialized before requesting permission
    const initialized = await initializeFirebase();
    if (!initialized || !messaging) {
      console.log('Firebase messaging not initialized');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Get FCM token
    // VAPID key should be set from Firebase Console > Project Settings > Cloud Messaging
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

    if (!vapidKey) {
      console.warn('VAPID key not configured');
      return null;
    }

    const { getToken } = await import('@firebase/messaging');
    const token = await getToken(messaging, { vapidKey });

    if (token) {
      console.log('FCM token obtained');
      return token;
    } else {
      console.log('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting notification permission:', error);
    // intentional: permission/token failure returns null; caller (iosPush.ts) surfaces error to user
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = async (
  callback: (payload: { notification?: { title?: string; body?: string; image?: string }; data?: Record<string, string> }) => void
): Promise<(() => void) | null> => {
  try {
    const initialized = await initializeFirebase();
    if (!initialized || !messaging) {
      return null;
    }

    const { onMessage } = await import('@firebase/messaging');
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up foreground message listener:', error);
    // intentional: background listener setup failure, app continues without foreground push display
    return null;
  }
};

// Check if notifications are supported
export const isNotificationSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
};


export { app, messaging };
